import { SingleContentScenarioMessage } from '@inbox-demo-nx/types';
import { mapValues } from 'remeda';

import { convertScenarioMessageTimestamp } from './convertScenarioMessageTimestamp';

function replaceVariables(value: string, context: StringVariablesContext) {
  const replaceTokens = (token: string) => {
    if (token.startsWith('{{') && token.endsWith('}}')) {
      const key = token.slice(2, -2);
      if (key.startsWith('channelMetaData.')) {
        return context.channelMetaData[key.replace(/^channelMetaData\./, '')];
      }
      if (key.startsWith('userMetaData.')) {
        return context.userMetaData[key.replace(/^userMetaData\./, '')];
      }
    }
    return token;
  };
  const tokens = value.split(/(\{\{[A-Za-z0-9.]+\}\})/g);
  return tokens.map(replaceTokens).join('');
}

function replaceDataVariables(
  data: Record<string, unknown>,
  context: StringVariablesContext
): Record<string, unknown> {
  return mapValues(data, (value) => {
    if (typeof value === 'string') {
      return replaceVariables(value, context);
    } else if (typeof value === 'object' && value != null) {
      return Array.isArray(value)
        ? value.map((v) =>
            typeof v === 'string'
              ? replaceVariables(v, context)
              : replaceDataVariables(v, context)
          )
        : replaceDataVariables(value as Record<string, unknown>, context);
    } else {
      return value;
    }
  });
}

export type StringVariablesContext = {
  channelMetaData: Record<string, string>;
  userMetaData: Record<string, string>;
};

export function convertScenarioMessageToMessagePayload(
  scenarioMessage: SingleContentScenarioMessage,
  context: { myUserId: string } & StringVariablesContext
) {
  const { sender, content, createdAt, data, isSilent, customType } =
    scenarioMessage;
  const { myUserId, ...variablesContext } = context;

  const commonProps = {
    created_at: convertScenarioMessageTimestamp(createdAt) || undefined, // if createdAt is 0, send the message immediately
    is_silent: isSilent,
    custom_type: customType,
    data: data && JSON.stringify(replaceDataVariables(data, variablesContext)),
  };

  if (!sender) {
    // Send as admin message
    return {
      ...commonProps,
      message_type: 'ADMM' as const,
      message: replaceVariables(String(content), variablesContext),
    };
  }

  const user_id = sender === 'ME' ? myUserId : sender;
  if (typeof content === 'object') {
    return {
      ...commonProps,
      user_id,
      message_type: 'FILE' as const,
      url: content.url,
      file_type: content.type,
      thumbnails: content.imageDimension
        ? [
            {
              real_width: content.imageDimension.width,
              real_height: content.imageDimension.height,
              url: content.url,
            },
          ]
        : undefined,
    };
  } else {
    return {
      ...commonProps,
      user_id,
      message_type: 'MESG' as const,
      message: replaceVariables(content, variablesContext),
    };
  }
}
